import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Link2, Unlink, RefreshCw, CheckCircle2, Loader2, Shield, ExternalLink, Download } from 'lucide-react';
import SectionNav from '../../../components/layout/SectionNav';
import PageHeader from '../../../components/layout/PageHeader';
import { portfolioTabs } from '../../../nav/publicNav';
import { usePortfolioStore } from '../../../state/portfolioStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

const BROKER_DISPLAY = {
  zerodha: { name: 'Zerodha', color: 'bg-blue-600', logo: 'Z', description: 'Connect your Zerodha Kite account to sync holdings automatically' },
  upstox: { name: 'Upstox', color: 'bg-purple-600', logo: 'U', description: 'Link your Upstox account for real-time portfolio tracking' },
  angelone: { name: 'Angel One', color: 'bg-red-600', logo: 'A', description: 'Sync your Angel One portfolio with one click' },
  groww: { name: 'Groww', color: 'bg-green-600', logo: 'G', description: 'Connect Groww to import your mutual fund and stock holdings' },
};

export default function BrokerIntegrationPage() {
  const importBrokerHoldings = usePortfolioStore((s) => s.importBrokerHoldings);

  const [brokers, setBrokers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [holdings, setHoldings] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectingBroker, setConnectingBroker] = useState(null);
  const [syncingBroker, setSyncingBroker] = useState(null);
  const [importingBroker, setImportingBroker] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [brokersRes, connRes] = await Promise.all([
        fetch(`${API}/broker/list`),
        fetch(`${API}/broker/connections`),
      ]);
      const [bData, cData] = await Promise.all([brokersRes.json(), connRes.json()]);
      setBrokers(bData.brokers || []);
      setConnections(cData.connections || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConnect = async (brokerKey) => {
    setConnectingBroker(brokerKey);
    try {
      const res = await fetch(`${API}/broker/auth-url?broker=${brokerKey}`);
      const data = await res.json();
      if (data.auth_url) {
        window.open(data.auth_url, '_blank', 'width=600,height=700');
      } else if (data.detail) {
        alert(data.detail);
      }
    } catch { alert('Failed to initiate connection'); }
    setConnectingBroker(null);
  };

  const handleSync = async (brokerKey) => {
    setSyncingBroker(brokerKey);
    try {
      const res = await fetch(`${API}/broker/sync?broker=${brokerKey}`, { method: 'POST' });
      const data = await res.json();
      if (data.status === 'synced') {
        const holdingsRes = await fetch(`${API}/broker/holdings?broker=${brokerKey}`);
        const holdingsData = await holdingsRes.json();
        setHoldings(prev => ({ ...prev, [brokerKey]: holdingsData.holdings || [] }));
        await fetchData();
      }
    } catch { alert('Sync failed'); }
    setSyncingBroker(null);
  };

  const handleImport = async (brokerKey) => {
    setImportingBroker(brokerKey);
    const result = await importBrokerHoldings(brokerKey);
    if (result.imported > 0) {
      alert(`Imported ${result.imported} holdings into your portfolio!`);
    } else {
      alert(result.message || 'No holdings to import. Try syncing first.');
    }
    setImportingBroker(null);
  };

  const handleDisconnect = async (brokerKey) => {
    const name = BROKER_DISPLAY[brokerKey]?.name || brokerKey;
    if (!window.confirm(`Disconnect ${name}?`)) return;
    try {
      await fetch(`${API}/broker/disconnect?broker=${brokerKey}`, { method: 'DELETE' });
      setHoldings(prev => { const n = { ...prev }; delete n[brokerKey]; return n; });
      await fetchData();
    } catch { /* silent */ }
  };

  const getConnection = (brokerKey) => connections.find(c => c.broker === brokerKey);

  if (loading) {
    return (
      <div className="space-y-4">
        <SectionNav tabs={portfolioTabs} />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionNav tabs={portfolioTabs} />
      <PageHeader
        title="Broker Integration"
        description="Connect your brokerage accounts to automatically sync portfolio holdings"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {brokers.map((broker) => {
          const display = BROKER_DISPLAY[broker.key] || { name: broker.name, color: 'bg-gray-600', logo: broker.key[0].toUpperCase() };
          const conn = getConnection(broker.key);
          const connected = conn?.status === 'connected';
          const connecting = connectingBroker === broker.key;
          const syncing = syncingBroker === broker.key;
          const importing = importingBroker === broker.key;

          return (
            <Card key={broker.key} className={`transition-all ${connected ? 'ring-2 ring-emerald-500' : ''}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl ${display.color} flex items-center justify-center text-white text-xl font-bold shrink-0`}>
                    {display.logo}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{display.name}</h3>
                      {connected && (
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-[10px]">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
                        </Badge>
                      )}
                      {!broker.has_credentials && (
                        <Badge variant="secondary" className="text-[10px]">No API Key</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">{display.description}</p>

                    {connected && conn?.user_name && (
                      <p className="text-[10px] text-muted-foreground mb-2">
                        Logged in as: {conn.user_name}
                        {conn.last_synced && ` · Last synced: ${new Date(conn.last_synced).toLocaleDateString()}`}
                        {conn.holdings_count > 0 && ` · ${conn.holdings_count} holdings`}
                      </p>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      {connected ? (
                        <>
                          <Button size="sm" variant="outline" className="gap-1 text-xs" disabled={syncing} onClick={() => handleSync(broker.key)}>
                            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            {syncing ? 'Syncing...' : 'Sync'}
                          </Button>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 text-xs" disabled={importing} onClick={() => handleImport(broker.key)}>
                            {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                            Import to Portfolio
                          </Button>
                          <Button size="sm" variant="ghost" className="gap-1 text-xs text-red-500" onClick={() => handleDisconnect(broker.key)}>
                            <Unlink className="w-3 h-3" /> Disconnect
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" onClick={() => handleConnect(broker.key)}
                          disabled={connecting || !broker.supports_oauth}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs">
                          {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                          {connecting ? 'Redirecting...' : broker.supports_oauth ? 'Connect via OAuth' : 'Coming Soon'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {holdings[broker.key]?.length > 0 && (
                  <div className="mt-4 border-t border-border pt-3">
                    <p className="text-xs font-semibold mb-2">Holdings ({holdings[broker.key].length})</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {holdings[broker.key].map((h, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px] py-1 px-2 rounded hover:bg-muted/50">
                          <span className="font-medium">{h.symbol}</span>
                          <span className="text-muted-foreground">{h.quantity} x ₹{h.average_price?.toFixed(2)}</span>
                          <span className={`font-medium ${(h.pnl || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            ₹{h.current_value?.toFixed(0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="bg-muted/30">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold mb-1">Read-Only Access</p>
            <p className="text-xs text-muted-foreground">
              We use OAuth to securely access your holdings in read-only mode. Your login credentials are never stored. 
              You can revoke access anytime from your broker's app settings.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
